import { getLocale } from '@tov-plus/ui';
import { startTransition, useState } from 'react';
import { guestAppConfig } from '@tov-plus/config';
import {
  invalidateQueryGroups,
  setTovQueryData,
  useTovQuery,
  useTovQueryClient,
} from '@tov-plus/frontend-data';
import {
  TovButton,
  Textarea,
  TovField,
  msg,
  TovCard,
  PageStatus,
  useTovForm,
  useTovFormStore,
  useToast,
} from '@tov-plus/ui';
import { NavLink } from '@tov-plus/frontend-data';
import { guestApi } from '../app/api';
import { StatePanel } from '../components/StatePanel';
import { useGuestAccess } from '../contexts/GuestAccessContext';
import { guestQueryKeys } from '../lib/data/guestQueryKeys';
import { getGuestErrorMessage } from '../lib/errors/guestErrors';
import { formatGuestDateTime } from '../lib/localization/guestLocale';

const helpErrorMessage = () =>
  msg('pages.help_page.guest_help_could_not_be_loaded');

function getSupportValueLabel(value: string) {
  const labels: Record<string, string> = {
    drafted: msg('pages.help_page.support_values.drafted'),
    sent: msg('pages.help_page.support_values.sent'),
    low: msg('pages.help_page.support_values.low'),
    normal: msg('pages.help_page.support_values.normal'),
    high: msg('pages.help_page.support_values.high'),
    urgent: msg('pages.help_page.support_values.urgent'),
    open: msg('pages.help_page.support_values.open'),
    acknowledged: msg('pages.help_page.support_values.acknowledged'),
    in_progress: msg('pages.help_page.support_values.in_progress'),
    resolved: msg('pages.help_page.support_values.resolved'),
    queued: msg('pages.help_page.support_values.queued'),
    scheduled: msg('pages.help_page.support_values.scheduled'),
    processing: msg('pages.help_page.support_values.processing'),
    claimed_by_worker: msg('pages.help_page.support_values.claimed_by_worker'),
    triaged: msg('pages.help_page.support_values.triaged'),
    assigned: msg('pages.help_page.support_values.assigned'),
    waiting_on_guest: msg('pages.help_page.support_values.waiting_on_guest'),
    sent_to_provider: msg('pages.help_page.support_values.sent_to_provider'),
    delivered: msg('pages.help_page.support_values.delivered'),
    opened: msg('pages.help_page.support_values.opened'),
    clicked: msg('pages.help_page.support_values.clicked'),
    retry_pending: msg('pages.help_page.support_values.retry_pending'),
    failed: msg('pages.help_page.support_values.failed'),
    permanently_failed: msg(
      'pages.help_page.support_values.permanently_failed',
    ),
    cancelled: msg('pages.help_page.support_values.cancelled'),
    suppressed: msg('pages.help_page.support_values.suppressed'),
    created: msg('pages.help_page.support_values.created'),
    owner_assigned: msg('pages.help_page.support_values.owner_assigned'),
    escalated: msg('pages.help_page.support_values.escalated'),
    replied: msg('pages.help_page.support_values.replied'),
  };
  return labels[value] ?? value.replaceAll('_', ' ');
}

export function HelpPage() {
  const access = useGuestAccess();
  const notifications = useToast();
  const queryClient = useTovQueryClient();
  const [question, setQuestion] = useState(
    msg('pages.help_page.what_do_i_need_to_do_next_for_this_occasion'),
  );
  const [status, setStatus] = useState<PageStatus>(PageStatus.IDLE);
  const [inboxStatus, setInboxStatus] = useState<'idle' | 'saving'>('idle');
  const [replyStatus, setReplyStatus] = useState<string | null>(null);
  const replyForm = useTovForm({
    defaultValues: {
      replyDrafts: {} as Record<string, string>,
    },
  });
  const replyDrafts = useTovFormStore(
    replyForm.store,
    (state) => state.values.replyDrafts,
  );
  const [result, setResult] = useState<Awaited<
    ReturnType<typeof guestApi.executeAiTask>
  > | null>(null);
  const recentMessages =
    access.status === 'ready'
      ? access.experience.recentCommunications.slice(0, 3)
      : [];
  const faqSections =
    access.status === 'ready'
      ? access.experience.guestWebsite.sections.filter(
          (section) =>
            section.enabled &&
            ['faq', 'important_notes', 'contact_help'].includes(section.type),
        )
      : [];
  const supportInboxQuery = useTovQuery({
    queryKey:
      access.status === 'ready' && access.occasionSlug && access.guestToken
        ? guestQueryKeys.support(access.occasionSlug, access.guestToken)
        : guestQueryKeys.support('pending', 'pending'),
    queryFn: () =>
      guestApi.getSupportInbox(access.occasionSlug!, access.guestToken!),
    enabled: Boolean(
      access.status === 'ready' && access.occasionSlug && access.guestToken,
    ),
  });
  if (access.status !== 'ready') {
    return <StatePanel preset="unknown-error" />;
  }
  if (!access.occasionSlug || !access.guestToken) {
    return <StatePanel preset="access-unavailable" />;
  }
  const resolvedLocaleCode = getLocale();
  const occasionTimezone = access.experience.occasion.timezone;
  const occasionSlug = access.occasionSlug;
  const guestToken = access.guestToken;
  const recommendedTarget = access.experience.journeySummary.recommendedTarget;
  const supportRequests = supportInboxQuery.data?.items ?? [];
  const supportQueryKey = guestQueryKeys.support(occasionSlug, guestToken);
  const escalationContext = [
    `${msg('pages.help_page.context.occasion')}: ${occasionSlug}`,
    `${msg('pages.help_page.context.guest')}: ${access.experience.guestDisplayName}`,
    `${msg('pages.help_page.context.current_route')}: ${window.location.pathname}`,
    `${msg('pages.help_page.context.recommended_route')}: ${access.experience.journeySummary.recommendedTarget}`,
    `${msg('pages.help_page.context.question')}: ${question}`,
  ].join('\n');
  async function copySupportText(text: string, successMessage: string) {
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      notifications.error(msg('pages.help_page.copy_failed'));
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      notifications.success(successMessage);
    } catch {
      notifications.error(msg('pages.help_page.copy_failed'));
    }
  }
  async function submit() {
    setStatus(PageStatus.LOADING);
    try {
      const nextResult = await guestApi.executeAiTask(
        occasionSlug,
        guestToken,
        'guest_faq_answer',
        {
          question,
        },
      );
      startTransition(() => {
        setResult(nextResult);
        setStatus(PageStatus.READY);
      });
    } catch (error) {
      startTransition(() => {
        setStatus(PageStatus.ERROR);
      });
      notifications.error(getGuestErrorMessage(error, helpErrorMessage()));
    }
  }
  const output = result?.output ?? null;
  async function createSupportRequest(questionText: string) {
    setInboxStatus('saving');
    try {
      const created = await guestApi.createSupportRequest(
        occasionSlug,
        guestToken,
        {
          question: questionText,
          route: window.location.pathname,
          metadata: {
            recommended_target: recommendedTarget,
          },
        },
      );
      setTovQueryData<Awaited<ReturnType<typeof guestApi.getSupportInbox>>>(
        queryClient,
        supportQueryKey,
        (current) =>
          current
            ? {
                ...current,
                items: [created, ...current.items].slice(0, 12),
              }
            : current,
      );
      await invalidateQueryGroups(queryClient, [
        supportQueryKey,
        guestQueryKeys.messages(occasionSlug, guestToken),
        guestQueryKeys.bootstrap(occasionSlug, guestToken),
      ]);
      notifications.success(
        msg('pages.help_page.support_request_sent_to_planner_queue'),
      );
    } catch (error) {
      notifications.error(getGuestErrorMessage(error, helpErrorMessage()));
    } finally {
      setInboxStatus('idle');
    }
  }
  async function sendSupportReply(requestId: string) {
    const bodyText = replyDrafts[requestId]?.trim() ?? '';
    if (!bodyText) {
      notifications.error(
        msg('pages.help_page.support_follow_up_messages_cannot_be_empty'),
      );
      return;
    }
    setReplyStatus(requestId);
    try {
      const updated = await guestApi.replyToSupportRequest(
        occasionSlug,
        guestToken,
        requestId,
        {
          bodyText,
        },
      );
      setTovQueryData<Awaited<ReturnType<typeof guestApi.getSupportInbox>>>(
        queryClient,
        supportQueryKey,
        (current) =>
          current
            ? {
                ...current,
                items: current.items.map((item) =>
                  item.id === requestId ? updated : item,
                ),
              }
            : current,
      );
      await invalidateQueryGroups(queryClient, [
        supportQueryKey,
        guestQueryKeys.messages(occasionSlug, guestToken),
        guestQueryKeys.bootstrap(occasionSlug, guestToken),
      ]);
      replyForm.setFieldValue('replyDrafts', {
        ...replyDrafts,
        [requestId]: '',
      });
      notifications.success(
        msg('pages.help_page.follow_up_added_to_support_thread'),
      );
    } catch (error) {
      notifications.error(getGuestErrorMessage(error, helpErrorMessage()));
    } finally {
      setReplyStatus(null);
    }
  }
  return (
    <div className="page-grid">
      <TovCard variant="content" isWide className="hero-card">
        <p className="page-eyebrow">
          {msg('pages.access_page.pages_access_page_guest_help')}
        </p>
        <h2>
          {msg(
            'pages.help_page.pages_help_page_ask_a_question_using_only_the_information_visible',
          )}
        </h2>
        <div className="action-row">
          <TovButton asChild variant="secondary">
            <NavLink to="/schedule">
              {`${msg('general.actions.open')} ${msg('general.modules.schedule.name')}`}
            </NavLink>
          </TovButton>
          <TovButton asChild variant="secondary">
            <NavLink to="/today">
              {msg('pages.help_page.pages_help_page_today_view')}
            </NavLink>
          </TovButton>
          <TovButton asChild variant="secondary">
            <NavLink to="/rsvp">
              {msg('app.guest_workflows.review_rsvp')}
            </NavLink>
          </TovButton>
          <TovButton asChild variant="secondary">
            <NavLink to="/events">
              {msg('pages.help_page.pages_help_page_browse_events')}
            </NavLink>
          </TovButton>
        </div>
      </TovCard>

      <TovCard variant="content" isWide>
        <TovField
          className="search-form__field"
          label={msg('pages.help_page.pages_help_page_your_question')}
        >
          <Textarea
            rows={4}
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
          />
        </TovField>
        <div className="action-row">
          <TovButton
            disabled={status === PageStatus.LOADING}
            onClick={() => void submit()}
          >
            {status === PageStatus.LOADING
              ? msg('pages.help_page.pages_help_page_asking')
              : msg('pages.help_page.pages_help_page_ask_tov_help')}
          </TovButton>
        </div>
      </TovCard>

      {faqSections.length > 0 ? (
        <TovCard variant="content" isWide>
          <p className="page-eyebrow">
            {msg('pages.help_page.pages_help_page_published_help_and_faqs')}
          </p>
          <div className="detail-list detail-list--stacked">
            {faqSections.map((section) => (
              <article key={section.id}>
                <strong>
                  {section.anchor_label ??
                    (typeof section.content_payload.title === 'string'
                      ? section.content_payload.title
                      : section.type.replaceAll('_', ' '))}
                </strong>
                <span>
                  {typeof section.content_payload.summary === 'string'
                    ? section.content_payload.summary
                    : typeof section.content_payload.body === 'string'
                      ? section.content_payload.body
                      : msg(
                          'pages.help_page.pages_help_page_hosts_published_extra_guest_facing_help_for_this_topic',
                        )}
                </span>
              </article>
            ))}
          </div>
        </TovCard>
      ) : null}

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg('pages.help_page.pages_help_page_need_a_human')}
        </p>
        <h3>
          {msg(
            'pages.help_page.pages_help_page_escalate_when_the_answer_is_incomplete',
          )}
        </h3>
        <p>
          {msg(
            'pages.help_page.pages_help_page_if_this_page_cannot_answer_your_question_use',
          )}{' '}
        </p>
        <div className="action-row">
          <TovButton asChild variant="secondary">
            <a
              href={`mailto:${guestAppConfig.supportEmail}?subject=${encodeURIComponent(msg('pages.help_page.guest_help_request'))}&body=${encodeURIComponent(escalationContext)}`}
            >
              {msg('app.router.app_router_contact_support')}{' '}
            </a>
          </TovButton>
          <TovButton
            onClick={() => {
              void copySupportText(
                question,
                msg('pages.help_page.question_copied'),
              );
            }}
            variant="secondary"
          >
            {msg('pages.help_page.pages_help_page_copy_question')}{' '}
          </TovButton>
          <TovButton
            onClick={() => {
              void copySupportText(
                escalationContext,
                msg('pages.help_page.context_copied'),
              );
            }}
            variant="secondary"
          >
            {msg('pages.help_page.pages_help_page_copy_full_context')}{' '}
          </TovButton>
          <TovButton
            onClick={() => {
              void createSupportRequest(question);
            }}
            disabled={inboxStatus === 'saving' || question.trim().length === 0}
            variant="secondary"
          >
            {inboxStatus === 'saving'
              ? msg('components.shell.components_shell_saving')
              : msg('pages.help_page.pages_help_page_send_to_support_inbox')}
          </TovButton>
        </div>
        {recentMessages.length > 0 ? (
          <div className="detail-list detail-list--stacked">
            {recentMessages.map((item) => (
              <article key={item.delivery.id}>
                <strong>
                  {item.message?.subjectText ??
                    item.event?.displayName ??
                    msg('pages.help_page.pages_help_page_occasion_update')}
                </strong>
                <span>
                  {getSupportValueLabel(item.delivery.status)}
                  {item.event ? ` • ${item.event.displayName}` : ''}
                </span>
              </article>
            ))}
          </div>
        ) : (
          <p>
            {msg(
              'pages.help_page.pages_help_page_no_recent_planner_messages_are_attached_to_your',
            )}
          </p>
        )}
      </TovCard>

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg('pages.help_page.pages_help_page_support_inbox')}
        </p>
        {supportRequests.length > 0 ? (
          <div className="detail-list detail-list--stacked">
            {supportRequests.map((request) => (
              <article key={request.id}>
                <strong>{request.question}</strong>
                <span>
                  {formatGuestDateTime(
                    request.createdAt,
                    resolvedLocaleCode,
                    occasionTimezone,
                  )}{' '}
                  • {request.route}
                </span>
                <span>
                  {msg('general.fields.status.label')}{' '}
                  {getSupportValueLabel(request.status)}
                  {request.priority
                    ? ` • ${msg('pages.help_page.priority')} ${getSupportValueLabel(request.priority)}`
                    : ''}
                  {request.resolutionState
                    ? ` • ${getSupportValueLabel(request.resolutionState)}`
                    : ''}
                  {request.queueState
                    ? ` • ${msg('pages.help_page.queue')} ${getSupportValueLabel(request.queueState)}`
                    : ''}
                  {request.assignedOwner
                    ? ` • ${msg('pages.help_page.owner')} ${request.assignedOwner}`
                    : ''}
                </span>
                {request.acknowledgedAt ? (
                  <span>
                    {msg('pages.help_page.pages_help_page_acknowledged')}{' '}
                    {formatGuestDateTime(
                      request.acknowledgedAt,
                      resolvedLocaleCode,
                      occasionTimezone,
                    )}
                    {request.resolvedAt
                      ? ` • ${msg('pages.help_page.resolved')} ${formatGuestDateTime(request.resolvedAt, resolvedLocaleCode, occasionTimezone)}`
                      : ''}
                  </span>
                ) : null}
                {request.replies.length > 0 ? (
                  <div className="detail-list detail-list--stacked">
                    {request.replies.map((reply) => (
                      <article key={reply.id}>
                        <strong>{reply.author}</strong>
                        <span>{reply.body}</span>
                        <span>
                          {msg('components.shell.sent')}{' '}
                          {formatGuestDateTime(
                            reply.sentAt,
                            resolvedLocaleCode,
                            occasionTimezone,
                          )}
                        </span>
                      </article>
                    ))}
                  </div>
                ) : null}
                {request.resolutionEvents.length > 0 ? (
                  <div className="detail-list detail-list--stacked">
                    {request.resolutionEvents.slice(-3).map((event) => (
                      <article key={event.id}>
                        <strong>{getSupportValueLabel(event.type)}</strong>
                        <span>{event.body}</span>
                        <span>
                          {formatGuestDateTime(
                            event.createdAt,
                            resolvedLocaleCode,
                            occasionTimezone,
                          )}
                        </span>
                      </article>
                    ))}
                  </div>
                ) : null}
                <TovField
                  className="search-form__field"
                  label={msg('pages.help_page.pages_help_page_add_follow_up')}
                >
                  <Textarea
                    rows={2}
                    value={replyDrafts[request.id] ?? ''}
                    onChange={(event) =>
                      replyForm.setFieldValue('replyDrafts', {
                        ...replyDrafts,
                        [request.id]: event.target.value,
                      })
                    }
                  />
                </TovField>
                <div className="action-row">
                  <TovButton
                    onClick={() => {
                      void sendSupportReply(request.id);
                    }}
                    disabled={replyStatus === request.id}
                    variant="secondary"
                  >
                    {replyStatus === request.id
                      ? msg('components.shell.components_shell_sending')
                      : msg('pages.help_page.pages_help_page_send_follow_up')}
                  </TovButton>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p>
            {msg(
              'pages.help_page.pages_help_page_save_questions_here_before_escalating_so_the_invite',
            )}{' '}
          </p>
        )}
      </TovCard>

      {result ? (
        <>
          <TovCard variant="content" isWide>
            <p className="page-eyebrow">
              {msg('pages.help_page.pages_help_page_answer')}
            </p>
            <p>
              {String(
                output?.answer ??
                  msg('pages.help_page.pages_help_page_no_answer_available'),
              )}
            </p>
            {Array.isArray(output?.supporting_facts) &&
            output.supporting_facts.length > 0 ? (
              <div className="detail-list detail-list--stacked">
                {output.supporting_facts.map((item) => (
                  <article key={String(item)}>
                    <strong>
                      {msg('pages.help_page.pages_help_page_grounding_fact')}
                    </strong>
                    <span>{String(item)}</span>
                  </article>
                ))}
              </div>
            ) : null}
          </TovCard>

          <TovCard variant="content">
            <p className="page-eyebrow">
              {msg('pages.help_page.pages_help_page_execution')}
            </p>
            <div className="detail-list">
              <article>
                <strong>{result.execution.runtime_mode}</strong>
                <span>
                  {msg('pages.help_page.pages_help_page_runtime_mode')}
                </span>
              </article>
              <article>
                <strong>{result.execution.status}</strong>
                <span>
                  {msg('pages.help_page.pages_help_page_result_status')}
                </span>
              </article>
              <article>
                <strong>
                  {result.execution.fallback_used
                    ? msg('general.booleans.yes')
                    : msg('general.booleans.no')}
                </strong>
                <span>
                  {msg('pages.help_page.pages_help_page_fallback_used')}
                </span>
              </article>
            </div>
          </TovCard>

          <TovCard variant="content">
            <p className="page-eyebrow">
              {msg('pages.help_page.pages_help_page_safety')}
            </p>
            <p>{result.safety.disclaimer}</p>
            {result.execution.fallback_reason ? (
              <p>{result.execution.fallback_reason}</p>
            ) : null}
            {result.execution.fallback_used ? (
              <p>
                {msg(
                  'pages.help_page.pages_help_page_if_this_answer_still_feels_incomplete_use_the',
                )}{' '}
              </p>
            ) : null}
          </TovCard>
        </>
      ) : null}
    </div>
  );
}
